import React from 'react'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import ProBadge from 'components/common/ProBadge'
import { selectionTypes } from 'constants.js'
import { startProCheckout } from '../../../proCheckout'

const PoiSaveButtons = ({ dataStore, uiStore, selectionStore, t }) => {
  const poi = dataStore && dataStore.poi

  if (!poi) {
    return null
  }

  const hasUnsavedPoiResult = (() => {
    if (!poi.temporaryMarkers || poi.temporaryMarkers.length === 0) return false
    return poi.temporaryMarkers.some(m => {
      if (!m) return false
      const idStr = String(m.id || '')
      const isSavedId = idStr.startsWith('saved_')
      return !m.savedMarkerId && !isSavedId
    })
  })()

  // Don't render if there are no bucket changes and no unsaved POI results
  if (!poi.hasBucketChanges && !hasUnsavedPoiResult) {
    return null
  }

  // Determine if a target group is directly selected (vs a shape within a group)
  const isTargetGroupSelected = selectionStore && selectionStore.type === selectionTypes.TARGET_GROUP

  const getTargetGroup = () => {
    let group = null
    if (isTargetGroupSelected) {
      group = selectionStore.item
    } else {
      group = selectionStore && selectionStore.data && selectionStore.data.group
    }
    if (group && dataStore && dataStore.targetGroups) {
      return dataStore.targetGroups.find(tg => tg.pk === group.pk) || group
    }
    return group
  }

  const user = uiStore && uiStore.user
  const isPro = user && user.isPro

  // Check if target group is selected (for disabling buttons)
  const targetGroup = getTargetGroup()
  const hasTargetGroup = !!targetGroup

  const handleSaveClick = async (e) => {
    if (!isPro) {
      if (uiStore) {
        uiStore.showSaveToFolderProTooltip = false
        uiStore.showSaveProTooltip = !uiStore.showSaveProTooltip
      }
      return
    }
    if (poi) {
      const targetGroup = getTargetGroup()
      if (targetGroup) {
        if (uiStore) uiStore.isPoiSaving = true
        try {
          const prevSelectionType = selectionStore && selectionStore.type
          const prevSelectionItem = selectionStore && selectionStore.item
          const prevSelectionGroup = selectionStore && selectionStore.data && selectionStore.data.group
          const unsavedMarkersBeforeSave = poi.temporaryMarkers.filter(m => {
            if (!m) return false
            const idStr = String(m.id || '')
            const isSavedId = idStr.startsWith('saved_')
            return !m.savedMarkerId && !isSavedId
          })
          await poi.saveBucketsToExistingGroup(targetGroup)
          await targetGroup.loadSavedMarkers()
          const EPS = 1e-4
          // Update temporary markers with savedMarkerId from newly saved markers
          if (targetGroup.savedMarkers && poi.temporaryMarkers) {
            poi.temporaryMarkers.forEach(tempMarker => {
              if (!tempMarker || !tempMarker.position || !tempMarker.position.latLng) return
              const tempLat = tempMarker.position.latLng.lat
              const tempLng = tempMarker.position.latLng.lng
              if (tempLat == null || tempLng == null) return
              const idStr = String(tempMarker.id || '')
              const isSavedRow = tempMarker.savedMarkerId || idStr.startsWith('saved_')
              let matchingSavedMarker = null
              if (isSavedRow) {
                const sid = tempMarker.savedMarkerId ||
                  (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : null)
                if (sid != null && Number.isFinite(sid)) {
                  matchingSavedMarker = targetGroup.savedMarkers.find(sm => sm.id === sid)
                }
              } else {
                const placeKeys = new Set()
                if (tempMarker.place_id) placeKeys.add(String(tempMarker.place_id))
                if (tempMarker.placeId) placeKeys.add(String(tempMarker.placeId))
                if (tempMarker.id) placeKeys.add(String(tempMarker.id))
                matchingSavedMarker = targetGroup.savedMarkers.find(sm => {
                  const smPid = sm.place_id != null ? String(sm.place_id) : (sm.placeId != null ? String(sm.placeId) : '')
                  return smPid && placeKeys.has(smPid)
                })
                if (!matchingSavedMarker) {
                  const atCoords = (targetGroup.savedMarkers || []).filter(sm => {
                    const smLat = sm.position && sm.position.latitude
                    const smLng = sm.position && sm.position.longitude
                    return smLat != null && smLng != null &&
                      Math.abs(smLat - tempLat) < EPS && Math.abs(smLng - tempLng) < EPS
                  })
                  if (atCoords.length === 1) {
                    matchingSavedMarker = atCoords[0]
                  }
                }
              }
              if (matchingSavedMarker) {
                tempMarker.savedMarkerId = matchingSavedMarker.id
                tempMarker.id = `saved_${matchingSavedMarker.id}`
                tempMarker.is_general = matchingSavedMarker.is_general
                tempMarker.is_ally = matchingSavedMarker.is_ally
                tempMarker.is_competitor = matchingSavedMarker.is_competitor
              }
            })
          }
          // Remove unsaved markers that weren't saved
          const savedMarkerIds = new Set()
          unsavedMarkersBeforeSave.forEach(unsavedMarker => {
            const unsavedLat = unsavedMarker.position && unsavedMarker.position.latLng && unsavedMarker.position.latLng.lat
            const unsavedLng = unsavedMarker.position && unsavedMarker.position.latLng && unsavedMarker.position.latLng.lng
            if (unsavedLat == null || unsavedLng == null) return
            const placeKeys = new Set()
            if (unsavedMarker.place_id) placeKeys.add(String(unsavedMarker.place_id))
            if (unsavedMarker.placeId) placeKeys.add(String(unsavedMarker.placeId))
            if (unsavedMarker.id) placeKeys.add(String(unsavedMarker.id))
            let wasSaved = false
            if (placeKeys.size > 0 && targetGroup.savedMarkers) {
              wasSaved = targetGroup.savedMarkers.some(sm => {
                const smPid = sm.place_id != null ? String(sm.place_id) : (sm.placeId != null ? String(sm.placeId) : '')
                return smPid && placeKeys.has(smPid)
              })
            }
            if (!wasSaved && targetGroup.savedMarkers) {
              const atCoords = targetGroup.savedMarkers.filter(sm => {
                const smLat = sm.position && sm.position.latitude
                const smLng = sm.position && sm.position.longitude
                return smLat != null && smLng != null &&
                  Math.abs(smLat - unsavedLat) < EPS && Math.abs(smLng - unsavedLng) < EPS
              })
              wasSaved = atCoords.length === 1
            }
            if (wasSaved && unsavedMarker.id) {
              savedMarkerIds.add(unsavedMarker.id)
            }
          })
          // Filter out unsaved markers that weren't saved
          poi.temporaryMarkers = poi.temporaryMarkers.filter(m => {
            if (!m || !m.id) return true
            const idStr = String(m.id)
            const isSavedId = idStr.startsWith('saved_')
            return isSavedId || m.savedMarkerId || !savedMarkerIds.has(m.id)
          })
          // Clean up bucket maps for removed markers
          savedMarkerIds.forEach(id => {
            if (!poi.temporaryMarkers.some(m => m.id === id || String(m.id).startsWith('saved_'))) {
              poi.bucketById.delete(id)
              poi.pendingBucketById.delete(id)
              poi.disabledRowsById.delete(id)
              poi.removedFromBucketById.delete(id)
            }
          })
          poi.resetBucketChanges()
          poi.syncBucketsFromSavedMarkers(targetGroup)
          await targetGroup.loadGeometries()
          await targetGroup.loadBoundaries()
          await targetGroup.loadSavedMarkers()
          targetGroup.expanded = true
          // Re-selecting the whole target group zooms the map to combined bounds and drops shape context.
          // After saving from a shape, keep the shape selected so other POIs stay untouched and the map stays put.
          const shapeSelectionTypes = new Set([
            selectionTypes.GEOPOLYGON,
            selectionTypes.POINT_BUFFER,
            selectionTypes.LINE,
            selectionTypes.BOUNDARY,
            selectionTypes.ISOCHRONE,
            selectionTypes.ZIP_CODE_BOUNDARY
          ])
          const wasShapeInThisGroup =
            prevSelectionItem &&
            prevSelectionItem.geoJSON &&
            shapeSelectionTypes.has(prevSelectionType) &&
            prevSelectionGroup &&
            targetGroup &&
            prevSelectionGroup.pk === targetGroup.pk
          if (!wasShapeInThisGroup) {
            // Refresh folder selection without switching the dashboard to People.
            selectionStore.select(targetGroup, { skipPeopleTab: true })
          }
          uiStore.addNotification({
            text: t('POI.SAVE_BUCKETS_SUCCESS'),
            isError: false,
            category: 'Success'
          })
        } catch (error) {
          uiStore.addNotification({
            text: t('POI.SAVE_BUCKETS_ERROR', { error: error.message || 'Unknown error' }),
            isError: true,
            category: 'Error'
          })
        } finally {
          if (uiStore) uiStore.isPoiSaving = false
        }
      } else {
        uiStore.addNotification({
          text: t('POI.NO_TARGET_GROUP'),
          isError: true,
          category: 'Error'
        })
      }
    }
  }

  const handleSaveToFolderClick = (e) => {
    if (!isPro) {
      if (uiStore) {
        uiStore.showSaveProTooltip = false
        uiStore.showSaveToFolderProTooltip = !uiStore.showSaveToFolderProTooltip
      }
      return
    }
    if (poi) {
      uiStore.saveModalOpenSaveToFolderModal({
        onConfirm: async () => {
          const folderOption = uiStore.saveModalFolderOption
          const rawFolderName = folderOption === 'new'
            ? uiStore.saveModalNewFolderName
            : uiStore.saveModalSelectedFolder
          const folderName = rawFolderName && rawFolderName.trim()

          if (!folderName) {
            uiStore.addNotification({
              text: t('POI.SAVE_FOLDER_NAME_REQUIRED'),
              isError: true,
              category: 'Error'
            })
            return
          }

          uiStore.isPoiSaving = true
          try {
            let targetGroupId = null
            if (folderOption === 'new') {
              targetGroupId = await dataStore.addTargetGroup({ name: folderName, text: '' })
            } else {
              const existingGroup = dataStore.targetGroups.find(group => group && group.name === folderName)
              if (!existingGroup) {
                uiStore.addNotification({
                  text: t('POI.SAVE_FOLDER_NOT_FOUND'),
                  isError: true,
                  category: 'Error'
                })
                uiStore.isPoiSaving = false
                return
              }
              targetGroupId = existingGroup.pk
            }

            if (!targetGroupId) {
              uiStore.addNotification({
                text: t('POI.NO_TARGET_GROUP'),
                isError: true,
                category: 'Error'
              })
              uiStore.isPoiSaving = false
              return
            }

            const selectedType = selectionStore && selectionStore.type
            const isTargetGroup = selectedType === selectionTypes.TARGET_GROUP
            const isShape = selectedType === selectionTypes.GEOPOLYGON || selectedType === selectionTypes.BOUNDARY
            const isMarker = selectedType === selectionTypes.SAVED_MARKER
            const hasSelection = isTargetGroup || isShape || isMarker

            // Only move markers currently displayed in POI menu (not all markers from selected group)
            const displayedMarkers = (poi.temporaryMarkers || []).filter(m => m != null)
            const displayedMarkerIds = new Set(displayedMarkers.map(m => m.id).filter(id => id != null))
            if (displayedMarkerIds.size > 0) {
              await dataStore.loadTargetGroups()
              const destinationGroup = dataStore.targetGroups &&
                dataStore.targetGroups.find(group => group && group.pk === targetGroupId)
              if (!destinationGroup) {
                uiStore.addNotification({
                  text: t('POI.SAVE_FOLDER_NOT_FOUND'),
                  isError: true,
                  category: 'Error'
                })
                uiStore.isPoiSaving = false
                return
              }
              await destinationGroup.loadSavedMarkers()
              const originalBucketById = new Map(poi.bucketById)
              const originalTemporaryMarkers = [...(poi.temporaryMarkers || [])]
              const filteredBucketById = new Map()
              originalBucketById.forEach((buckets, id) => {
                if (displayedMarkerIds.has(id)) {
                  filteredBucketById.set(id, buckets)
                }
              })
              const filteredTemporaryMarkers = originalTemporaryMarkers.filter(m => {
                if (!m || !m.id) return false
                return displayedMarkerIds.has(m.id)
              })
              poi.bucketById = filteredBucketById
              poi.temporaryMarkers = filteredTemporaryMarkers

              // Use mergeOnly: true to PREVENT deleting existing markers in the destination group
              await poi.saveBucketsToExistingGroup(destinationGroup, { mergeOnly: true })
              uiStore.saveModalCloseModal()
              // Drop the save spinner before the toast. .loader-overlay is
              // z-index 9999 and was covering the confirmation until it timed out.
              uiStore.isPoiSaving = false
              uiStore.addNotification({
                text: t('POI.SAVE_TO_FOLDER_SUCCESS', { folderName }),
                isError: false,
                category: 'Success'
              })

              try {
                // Update temporary markers with savedMarkerId from destination group
                await destinationGroup.loadSavedMarkers()
                const EPS = 1e-4
                if (destinationGroup.savedMarkers && poi.temporaryMarkers) {
                  poi.temporaryMarkers.forEach(tempMarker => {
                    if (!tempMarker || !tempMarker.position || !tempMarker.position.latLng) return
                    const tempLat = tempMarker.position.latLng.lat
                    const tempLng = tempMarker.position.latLng.lng
                    if (tempLat == null || tempLng == null) return
                    const idStr = String(tempMarker.id || '')
                    const isSavedRow = tempMarker.savedMarkerId || idStr.startsWith('saved_')
                    let matchingSavedMarker = null
                    if (isSavedRow) {
                      const sid = tempMarker.savedMarkerId ||
                        (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : null)
                      if (sid != null && Number.isFinite(sid)) {
                        matchingSavedMarker = destinationGroup.savedMarkers.find(sm => sm.id === sid)
                      }
                    } else {
                      const placeKeys = new Set()
                      if (tempMarker.place_id) placeKeys.add(String(tempMarker.place_id))
                      if (tempMarker.placeId) placeKeys.add(String(tempMarker.placeId))
                      if (tempMarker.id) placeKeys.add(String(tempMarker.id))
                      matchingSavedMarker = destinationGroup.savedMarkers.find(sm => {
                        const smPid = sm.place_id != null ? String(sm.place_id) : (sm.placeId != null ? String(sm.placeId) : '')
                        return smPid && placeKeys.has(smPid)
                      })
                      if (!matchingSavedMarker) {
                        const atCoords = (destinationGroup.savedMarkers || []).filter(sm => {
                          const smLat = sm.position && sm.position.latitude
                          const smLng = sm.position && sm.position.longitude
                          return smLat != null && smLng != null &&
                            Math.abs(smLat - tempLat) < EPS && Math.abs(smLng - tempLng) < EPS
                        })
                        if (atCoords.length === 1) matchingSavedMarker = atCoords[0]
                      }
                    }
                    if (matchingSavedMarker) {
                      tempMarker.savedMarkerId = matchingSavedMarker.id
                      tempMarker.id = `saved_${matchingSavedMarker.id}`
                      tempMarker.is_general = matchingSavedMarker.is_general
                      tempMarker.is_ally = matchingSavedMarker.is_ally
                      tempMarker.is_competitor = matchingSavedMarker.is_competitor
                    }
                  })
                }
                poi.bucketById = originalBucketById
                poi.temporaryMarkers = originalTemporaryMarkers
                const savedMarkerIds = new Set()
                filteredTemporaryMarkers.forEach(m => {
                  if (m && m.id) savedMarkerIds.add(m.id)
                })
                poi.temporaryMarkers = poi.temporaryMarkers.filter(m => {
                  if (!m || !m.id) return true
                  const idStr = String(m.id)
                  const isSavedId = idStr.startsWith('saved_')
                  return isSavedId || m.savedMarkerId || !savedMarkerIds.has(m.id)
                })
                savedMarkerIds.forEach(id => {
                  if (!poi.temporaryMarkers.some(m => m.id === id || String(m.id).startsWith('saved_'))) {
                    poi.bucketById.delete(id)
                    poi.pendingBucketById.delete(id)
                    poi.disabledRowsById.delete(id)
                    poi.removedFromBucketById.delete(id)
                  }
                })
                poi.resetBucketChanges()
                await destinationGroup.loadGeometries()
                await destinationGroup.loadBoundaries()
                await destinationGroup.loadSavedMarkers()
                // Stay on the POI tab after Save to Folder; only refresh the destination folder.
                selectionStore.select(destinationGroup, { skipPeopleTab: true })
              } catch (refreshError) {
                console.warn('POI copy UI refresh failed:', refreshError)
              }
              return
            }
            if (hasSelection) {
              // Move markers based on selection: target group (all), shape (within shape), marker (single)
              try {
                await poi.moveMarkersToTargetGroup(dataStore, targetGroupId)
                uiStore.saveModalCloseModal()
                poi.resetBucketChanges()
                uiStore.isPoiSaving = false
                uiStore.addNotification({
                  text: t('POI.MOVE_TO_FOLDER_SUCCESS', { folderName }),
                  isError: false,
                  category: 'Success'
                })
              } catch (error) {
                uiStore.addNotification({
                  text: t('POI.MOVE_MARKERS_ERROR', { error: error.message || 'Unknown error' }),
                  isError: true,
                  category: 'Error'
                })
                uiStore.isPoiSaving = false
                return
              }
            } else {
              uiStore.addNotification({
                text: t('POI.MOVE_NO_SELECTION'),
                isError: true,
                category: 'Error'
              })
              uiStore.isPoiSaving = false
              return
            }

            await dataStore.loadTargetGroups()
            const targetGroup = dataStore.targetGroups &&
              dataStore.targetGroups.find(group => group && group.pk === targetGroupId)
            if (targetGroup) {
              await targetGroup.loadGeometries()
              await targetGroup.loadBoundaries()
              await targetGroup.loadSavedMarkers()
              selectionStore.select(targetGroup, { skipPeopleTab: true })
            }
          } catch (error) {
            uiStore.addNotification({
              text: t('POI.SAVE_ITEM_ERROR', { error: error.message || 'Unknown error' }),
              isError: true,
              category: 'Error'
            })
          } finally {
            uiStore.isPoiSaving = false
          }
        }
      })
    }
  }

  const handleUpgradeClick = async (e) => {
    e.preventDefault()
    await startProCheckout({ uiStore, t })
  }

  return (
    <div className='search-results__save-wrapper'>
      <div className='search-results__save-button-wrapper'>
        <button
          type='button'
          className='search-results__save-button'
          onClick={handleSaveClick}
          disabled={!hasTargetGroup}
        >
          <span>{t('GLOBAL.CONFIRM_SAVE')}</span>
          {!isPro && (
            <ProBadge className='search-results__pro text-small'>{t('GLOBAL.PRO')}</ProBadge>
          )}
        </button>
        {!isPro && uiStore && uiStore.showSaveProTooltip && (
          <div className='search-results__pro-tooltip'>
            <div className='search-results__pro-tooltip-title'>
              <strong>{t('POI.PRO_TOOLTIP_TITLE')}</strong>
            </div>
            <div className='search-results__pro-tooltip-text'>
              {t('POI.PRO_TOOLTIP_TEXT')}
            </div>
            <a
              className='search-results__pro-tooltip-upgrade'
              target='_blank'
              rel='noopener noreferrer'
              onClick={handleUpgradeClick}
            >
              {t('GLOBAL.UPGRADE_PRO')}
            </a>
          </div>
        )}
      </div>
      <div className='search-results__save-button-wrapper'>
        <button
          type='button'
          className='search-results__save-button search-results__save-button--outlined'
          onClick={handleSaveToFolderClick}
        >
          <span>{t('MODAL.SAVE_TO_FOLDER_TITLE')}</span>
          {!isPro && (
            <ProBadge className='search-results__pro text-small'>{t('GLOBAL.PRO')}</ProBadge>
          )}
        </button>
        {!isPro && uiStore && uiStore.showSaveToFolderProTooltip && (
          <div className='search-results__pro-tooltip'>
            <div className='search-results__pro-tooltip-title'>
              <strong>{t('POI.PRO_TOOLTIP_TITLE')}</strong>
            </div>
            <div className='search-results__pro-tooltip-text'>
              {t('POI.PRO_TOOLTIP_TEXT')}
            </div>
            <a
              className='search-results__pro-tooltip-upgrade'
              target='_blank'
              rel='noopener noreferrer'
              onClick={handleUpgradeClick}
            >
              {t('GLOBAL.UPGRADE_PRO')}
            </a>
          </div>
        )}
      </div>
      <button
        type='button'
        className='search-results__save-button search-results__save-button--outlined'
        onClick={() => {
          if (poi) {
            poi.cancelPoiChanges()
            uiStore.addNotification({
              text: t('POI.RESET_TO_BASELINE'),
              isError: false,
              category: 'Info'
            })
          }
        }}
      >
        {t('POI.RESET_BUTTON')}
      </button>
    </div>
  )
}

export default withTranslation()(
  inject('dataStore', 'uiStore', 'selectionStore')(observer(PoiSaveButtons))
)
